"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "@/lib/i18n-client";
import { Search, ArrowRight, ShieldCheck, ListFilter, CalendarClock } from "lucide-react";
import { ixTrack } from "../analytics";

/**
 * eSIM Index — comparison search hero per DESIGN_INSTRUCTIONS.md: a neutral
 * editorial "Compare travel eSIMs" search (destination / brand / term) routing
 * to the destination comparison index. Claim-safe, transparency-forward.
 */
export default function SearchHero() {
  const { t } = useTranslations();
  const router = useRouter();
  const [q, setQ] = useState("");

  const go = (e: React.FormEvent) => { e.preventDefault(); ixTrack.searchSubmitted(q.trim() || undefined); router.push("/destinations/"); };

  return (
    <section className="border-b border-border">
      <div className="container mx-auto px-4 py-12 lg:py-16 text-center max-w-3xl">
        <span className="ix-badge mb-4">{t("ix.hero.eyebrow", "Independent comparison")}</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl">{t("ix.hero.title", "Compare travel eSIMs in seconds.")}</h1>
        <p className="mt-3 text-lg text-muted-foreground">{t("ix.hero.sub", "Find the best eSIM for your destination by price, coverage, data size and ease of use — with a transparent methodology.")}</p>
        <form onSubmit={go} className="ix-card mt-7 p-2 flex items-center gap-2 max-w-xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ix.hero.placeholder", "Search a destination, brand or comparison")} aria-label={t("ix.hero.placeholder", "Search a destination, brand or comparison")} className="w-full h-11 rounded-lg bg-transparent pl-10 pr-3 text-foreground focus:outline-none" />
          </div>
          <button type="submit" className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground font-bold px-5 h-11 hover:bg-primary/90 transition-all active:scale-[0.98] shrink-0">{t("ix.hero.cta", "Compare")} <ArrowRight className="w-4 h-4" /></button>
        </form>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><ListFilter className="w-4 h-4 text-primary" />{t("ix.hero.f1", "Filter by price, data & coverage")}</span>
          <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-primary" />{t("ix.hero.f2", "Updated regularly")}</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" />{t("ix.hero.f3", "Transparent methodology")}</span>
        </div>
      </div>
    </section>
  );
}
